import { Component, Input } from '@angular/core';
import { FieldInstance } from '@workflow/class/field.service';
import { CoreControlType } from '@workflow/class/workflow.type';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { FieldSummary, FieldData } from '@workflow/ecp-workflow/dynamic-app/types/review-data.type';

@Component({
  selector: 'ecp-review-field',
  styleUrls: ['./review-field.component.scss'],
  templateUrl: './review-field.component.html'
})
export class ReviewFieldComponent {
  /**
   * compounded obj with field and display data
   */
  @Input() public fieldSummary: FieldSummary;

  // The Automation ID is a unique identifier for the field that is used to track the field in the UI.
  public automationId: string;

  public readonly optionalReviewFieldText = Info.optionalReviewFieldText;
  /**
   * construct displayed text
   */
  public get getData(): FieldData {
    const field = this.fieldSummary.field;
    if (
      field &&
      ['dont-have-liability', 'dont-have-asset', 'dont-have-liability-co-applicant', 'dont-have-asset-co-applicant'].includes(
        field.config.id
      ) &&
      !field.data
    ) {
      return { label: '', data: '' };
    }

    if (field && field.id === 'preferred-related-note') {
      const label = 'Please tell us here if you have other info we should know as we consider your loan';
      return this.fieldSummary.fieldData || { label, data: this.getValue(this.fieldSummary.field) };
    }
    // Some fields that are lacking an Automation ID and ID need to use label as a fallback.
    const tempLabel = this.fieldSummary?.field?.label || this.fieldSummary.fieldData?.label;
    this.automationId = this.fieldSummary?.field?.automationId ? this.fieldSummary.field.automationId : this.convertLabeltoId(tempLabel);

    return (
      this.fieldSummary.fieldData || {
        label: this.fieldSummary.field?.label,
        data: this.getValue(this.fieldSummary.field)
      }
    );
  }

  public fieldLayout(layout: string): boolean {
    const fieldLayout = this.getData.layout || 'common';
    return layout === fieldLayout;
  }

  /**
   * Convert a label to an ID by replacing spaces with hyphens and converting to lowercase.
   * @param label
   * @returns string
   */
  public convertLabeltoId(label: string): string {
    return label ? label.replace(/\s/g, '-').toLowerCase() : 'no-automation-id-available';
  }

  /**
   * Cleanse the field value based on type and presence of enumeration.
   */
  public getValue(field: FieldInstance): string | boolean {
    if (field.config.type === 'checkbox' || field.config.type === 'toggle') {
      // eslint-disable-next-line eqeqeq
      return field.data.value === true || (field.config.map != undefined && field.data.value === field.config.map.true) ? 'Yes' : 'No';
    } else if (field.config.content) {
      return field.content;
    } else if (
      this.fieldSummary.field.config.type === ('input-obfuscate' as CoreControlType) ||
      this.fieldSummary.field.config.type === ('input-mask' as CoreControlType)
    ) {
      if (this.fieldSummary.field.config.mask === 'ssn') {
        return field.data.value && field.data.value.toString() !== '' ? Info.ssnObfuscatedValue : '';
      } else if (this.fieldSummary.field.config.mask === 'ein') {
        return field.data.value && field.data.value.toString() !== '' ? Info.einObfuscatedValue : '';
      }
    }
    return field.data.value == null ? '' : field.readableValue; // ==null checks against null and undefined.
  }
}
